import { Archive, CheckCircle2, ClipboardCheck, Edit3, Plus, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { UniversalOperationalRecordDrawer } from "../../components/dashboard/UniversalOperationalRecordDrawer";
import { BusinessCenterOSAdminShell } from "../../components/layout/BusinessCenterOSAdminShell";
import { formatDate } from "../../lib/format";
import {
  appendUniversalActivity,
  appendUniversalAuditEntry,
  archiveUniversalBusinessNote,
  createUniversalActivityEvent,
  createUniversalAuditTrailEntry,
  createUniversalBusinessNote,
  createUniversalOperationalRecord,
  createUniversalTickler,
  registerOperationalRecord,
  registerOperationalRecords,
  updateUniversalTicklerStatus,
  upsertUniversalBusinessNote,
  upsertUniversalTickler,
  type UniversalBusinessNote,
  type UniversalOperationalFoundationState,
  type UniversalRecordType,
  type UniversalTickler,
  type UniversalVisibility,
  type UniversalWorkspaceKey,
} from "../../lib/universalOperationalEngines";
import { loadOperationalState, saveOperationalState } from "../../lib/universalOperationalPersistence";
import { buildSeedRegistryRecords, validationRecord, validationRegistryRecord } from "./universalOperationalRegistrySeeds";

type BusinessNoteRecordType = UniversalBusinessNote["recordType"];
type BusinessNoteCategory = UniversalBusinessNote["noteCategory"];
type DateRangeFilter = "all" | "today" | "week" | "month";
type ArchiveFilter = "active" | "archived" | "all";

const businessNoteCategories: BusinessNoteCategory[] = [
  "General",
  "Sales",
  "Client",
  "Project",
  "Onboarding",
  "Consulting",
  "Service Delivery",
  "Internal",
];

const workspaceOptions: Array<{ value: UniversalWorkspaceKey; label: string }> = [
  { value: "business-center-os", label: "Business Center OS" },
  { value: "truevance-connect-os", label: "Truevance Connect OS" },
  { value: "kreatethedate-os", label: "Kreatethedate OS" },
  { value: "memory-media-vault-os", label: "Memory Media Vault OS" },
  { value: "future-template", label: "Future Templates" },
];

const recordTypeOptions: Array<{ value: BusinessNoteRecordType; label: string }> = [
  { value: "lead", label: "Lead" },
  { value: "opportunity", label: "Opportunity" },
  { value: "client", label: "Client" },
  { value: "project", label: "Project" },
  { value: "task", label: "Task" },
  { value: "document", label: "Document" },
  { value: "vault_record", label: "Vault Record" },
  { value: "workflow_step", label: "Workflow Step" },
  { value: "letter_template", label: "Letter Template" },
  { value: "letter_draft", label: "Letter Draft" },
];

const visibilityOptions: Array<{ value: UniversalVisibility; label: string }> = [
  { value: "internal", label: "Internal" },
  { value: "manager_only", label: "Manager only" },
  { value: "client_visible", label: "Client visible" },
];

const blankNoteForm = {
  workspace: validationRecord.workspace as UniversalWorkspaceKey,
  recordType: validationRecord.recordType as BusinessNoteRecordType,
  recordId: validationRecord.recordId,
  noteCategory: "General" as BusinessNoteCategory,
  visibility: "internal" as UniversalVisibility,
  content: "",
};

export function UniversalBusinessNotesCenterPage() {
  const [operationalState, setOperationalState] = useState<UniversalOperationalFoundationState | null>(null);
  const [operationalStatus, setOperationalStatus] = useState("Loading operational state.");
  const [form, setForm] = useState(blankNoteForm);
  const [editingNoteId, setEditingNoteId] = useState("");
  const [query, setQuery] = useState("");
  const [workspaceFilter, setWorkspaceFilter] = useState<UniversalWorkspaceKey | "all">("all");
  const [recordTypeFilter, setRecordTypeFilter] = useState<BusinessNoteRecordType | "all">("all");
  const [categoryFilter, setCategoryFilter] = useState<BusinessNoteCategory | "all">("all");
  const [visibilityFilter, setVisibilityFilter] = useState<UniversalVisibility | "all">("all");
  const [authorFilter, setAuthorFilter] = useState("all");
  const [dateRangeFilter, setDateRangeFilter] = useState<DateRangeFilter>("all");
  const [archiveFilter, setArchiveFilter] = useState<ArchiveFilter>("active");
  const [selectedNoteId, setSelectedNoteId] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void loadOperationalState().then(async (result) => {
      if (cancelled) return;
      const seededRecords = await buildSeedRegistryRecords();
      const seededState = registerOperationalRecords(result.state, seededRecords);
      setOperationalState(seededState);
      if (seededRecords.some((record) => !result.state.operationalRecords.some((item) => item.id === record.id && item.updatedAt === record.updatedAt))) {
        void saveOperationalState(seededState);
      }
      setOperationalStatus(
        result.backendAvailable
          ? "Business notes are synced through the backend state store with local fallback."
          : "Backend sync is unavailable; business notes are saved locally in this browser.",
      );
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const state = operationalState;
  const notes = state?.businessNotes || [];
  const registryRecords = state?.operationalRecords.filter((record) => !record.archivedAt) || [];
  const selectedRegistryRecord =
    registryRecords.find((record) => record.workspace === form.workspace && record.recordType === form.recordType && record.id === form.recordId) ||
    null;
  const validationTicklers = state?.ticklers.filter(
    (tickler) =>
      tickler.workspace === validationRecord.workspace &&
      tickler.recordType === validationRecord.recordType &&
      tickler.recordId === validationRecord.recordId,
  ) || [];
  const selectedDrawerTickler = validationTicklers[0] || null;
  const selectedNote = notes.find((note) => note.id === selectedNoteId) || null;
  const authors = useMemo(() => Array.from(new Set(notes.map((note) => note.author).filter(Boolean))).sort(), [notes]);
  const now = new Date();
  const thisWeek = new Date(now.getTime() - 7 * 86400000);
  const thisMonth = new Date(now.getTime() - 30 * 86400000);
  const activeNotes = notes.filter((note) => note.status !== "archived");
  const archivedNotes = notes.filter((note) => note.status === "archived");
  const notesThisWeek = activeNotes.filter((note) => new Date(note.updatedAt || note.timestamp).getTime() >= thisWeek.getTime());

  const filteredNotes = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return notes
      .filter((note) => {
        const status = note.status || "active";
        if (archiveFilter !== "all" && status !== archiveFilter) return false;
        if (workspaceFilter !== "all" && note.workspace !== workspaceFilter) return false;
        if (recordTypeFilter !== "all" && note.recordType !== recordTypeFilter) return false;
        if (categoryFilter !== "all" && note.noteCategory !== categoryFilter) return false;
        if (visibilityFilter !== "all" && note.visibility !== visibilityFilter) return false;
        if (authorFilter !== "all" && note.author !== authorFilter) return false;
        const noteDate = new Date(note.updatedAt || note.timestamp);
        if (dateRangeFilter === "today" && noteDate.toISOString().slice(0, 10) !== now.toISOString().slice(0, 10)) return false;
        if (dateRangeFilter === "week" && noteDate.getTime() < thisWeek.getTime()) return false;
        if (dateRangeFilter === "month" && noteDate.getTime() < thisMonth.getTime()) return false;
        if (normalizedQuery) {
          const haystack = [note.content, note.recordId, note.author, note.workspace, note.recordType, note.noteCategory, note.visibility]
            .join(" ")
            .toLowerCase();
          if (!haystack.includes(normalizedQuery)) return false;
        }
        return true;
      })
      .sort((a, b) => new Date(b.updatedAt || b.timestamp).getTime() - new Date(a.updatedAt || a.timestamp).getTime());
  }, [archiveFilter, authorFilter, categoryFilter, dateRangeFilter, notes, query, recordTypeFilter, thisMonth, thisWeek, visibilityFilter, workspaceFilter, now,]);

  function updateOperationalState(mutator: (current: UniversalOperationalFoundationState) => UniversalOperationalFoundationState) {
    if (!state) return;
    const next = mutator(state);
    setOperationalState(next);
    void saveOperationalState(next).then((result) => {
      setOperationalStatus(
        result.backendAvailable
          ? "Business notes are synced through the backend state store with local fallback."
          : "Backend sync is unavailable; business notes are saved locally in this browser.",
      );
    });
  }

  function resetForm() {
    setForm(blankNoteForm);
    setEditingNoteId("");
  }

  function handleSelectRegistryRecord(recordId: string) {
    const record = registryRecords.find((item) => item.id === recordId);
    if (!record || !isBusinessNoteRecordType(record.recordType)) return;
    const recordType = record.recordType;
    setForm((current) => ({
      ...current,
      workspace: record.workspace,
      recordType,
      recordId: record.id,
    }));
  }

  function handleEditNote(note: UniversalBusinessNote) {
    setEditingNoteId(note.id);
    setSelectedNoteId(note.id);
    setForm({
      workspace: note.workspace,
      recordType: note.recordType,
      recordId: note.recordId,
      noteCategory: note.noteCategory,
      visibility: note.visibility,
      content: note.content,
    });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!state || !form.content.trim() || !form.recordId.trim()) return;
    const timestamp = new Date().toISOString();
    const existing = editingNoteId ? state.businessNotes.find((note) => note.id === editingNoteId) : null;
    const note = createUniversalBusinessNote({
      id: existing?.id,
      author: existing?.author || validationRecord.owner,
      timestamp: existing?.timestamp || timestamp,
      updatedAt: timestamp,
      workspace: form.workspace,
      recordType: form.recordType,
      recordId: form.recordId.trim(),
      noteCategory: form.noteCategory,
      visibility: form.visibility,
      content: form.content.trim(),
      status: "active",
    });
    const action = existing ? "Business note edited" : "Business note created";
    const activity = createUniversalActivityEvent({
      workspace: note.workspace,
      recordType: note.recordType,
      recordId: note.recordId,
      user: note.author,
      role: "operator",
      timestamp,
      action,
      description: note.content,
      outcome: note.noteCategory,
      visibility: note.visibility,
    });
    const audit = createUniversalAuditTrailEntry({
      user: note.author,
      timestamp,
      recordType: note.recordType,
      recordId: note.recordId,
      fieldChanged: existing ? "business_note.edit" : "business_note.create",
      previousValue: existing ? summarizeNote(existing) : "None",
      newValue: summarizeNote(note),
      workspace: note.workspace,
      visibility: note.visibility,
    });
    const registryRecord = createUniversalOperationalRecord({
      id: note.recordId,
      workspace: note.workspace,
      recordType: note.recordType,
      recordName: selectedRegistryRecord?.recordName || note.recordId,
      status: selectedRegistryRecord?.status || "active",
      owner: selectedRegistryRecord?.owner || note.author,
      relatedClientId: selectedRegistryRecord?.relatedClientId,
      relatedProjectId: selectedRegistryRecord?.relatedProjectId,
      route: selectedRegistryRecord?.route,
      source: selectedRegistryRecord?.source || "Business Notes Center",
      createdAt: selectedRegistryRecord?.createdAt || timestamp,
      updatedAt: timestamp,
    });
    updateOperationalState((current) =>
      appendUniversalAuditEntry(appendUniversalActivity(upsertUniversalBusinessNote(registerOperationalRecord(current, registryRecord), note), activity), audit),
    );
    setSelectedNoteId(note.id);
    resetForm();
  }

  function handleArchiveNote(note: UniversalBusinessNote) {
    const timestamp = new Date().toISOString();
    const activity = createUniversalActivityEvent({
      workspace: note.workspace,
      recordType: note.recordType,
      recordId: note.recordId,
      user: note.author,
      role: "operator",
      timestamp,
      action: "Business note archived",
      description: note.content,
      outcome: "Archived",
      visibility: note.visibility,
    });
    const audit = createUniversalAuditTrailEntry({
      user: note.author,
      timestamp,
      recordType: note.recordType,
      recordId: note.recordId,
      fieldChanged: "business_note.status",
      previousValue: note.status || "active",
      newValue: "archived",
      workspace: note.workspace,
      visibility: note.visibility,
    });
    updateOperationalState((current) => appendUniversalAuditEntry(appendUniversalActivity(archiveUniversalBusinessNote(current, note.id), activity), audit));
  }

  function handleCreateValidationTickler() {
    const timestamp = new Date().toISOString();
    const dueDate = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
    const tickler = createUniversalTickler({
      assignedUser: validationRecord.owner,
      dueDate,
      dueTime: "10:00",
      priority: "Medium",
      reminderType: "Follow-Up Reminder",
      notes: "Validate non-AR business note follow-up for the Business Center OS project record.",
      workspace: validationRecord.workspace,
      recordType: validationRecord.recordType,
      recordId: validationRecord.recordId,
    });
    const activity = createUniversalActivityEvent({
      workspace: tickler.workspace,
      recordType: tickler.recordType,
      recordId: tickler.recordId,
      user: tickler.assignedUser,
      role: "operator",
      timestamp,
      action: "Non-AR validation tickler created",
      description: tickler.notes,
      outcome: tickler.status,
      visibility: "internal",
    });
    const audit = createUniversalAuditTrailEntry({
      user: tickler.assignedUser,
      timestamp,
      recordType: tickler.recordType,
      recordId: tickler.recordId,
      fieldChanged: "tickler.create",
      previousValue: "None",
      newValue: tickler.notes,
      workspace: tickler.workspace,
      visibility: "internal",
    });
    updateOperationalState((current) =>
      appendUniversalAuditEntry(
        appendUniversalActivity(upsertUniversalTickler(registerOperationalRecord(current, validationRegistryRecord), tickler), activity),
        audit,
      ),
    );
  }

  function handleTicklerStatus(tickler: UniversalTickler, status: UniversalTickler["status"]) {
    const result = updateUniversalTicklerStatus(tickler, status, tickler.assignedUser);
    updateOperationalState((current) =>
      appendUniversalAuditEntry(appendUniversalActivity(upsertUniversalTickler(registerOperationalRecord(current, validationRegistryRecord), result.tickler), result.activityEvent), result.auditEntry),
    );
  }

  function handleDrawerNote(tickler: UniversalTickler, content: string) {
    if (!isBusinessNoteRecordType(tickler.recordType)) return;
    const timestamp = new Date().toISOString();
    const note = createUniversalBusinessNote({
      author: tickler.assignedUser,
      timestamp,
      updatedAt: timestamp,
      workspace: tickler.workspace,
      recordType: tickler.recordType,
      recordId: tickler.recordId,
      noteCategory: "General",
      visibility: "internal",
      content: content.trim(),
      status: "active",
    });
    const activity = createUniversalActivityEvent({
      workspace: note.workspace,
      recordType: note.recordType,
      recordId: note.recordId,
      user: note.author,
      role: "operator",
      timestamp,
      action: "Business note created",
      description: note.content,
      outcome: note.noteCategory,
      visibility: note.visibility,
    });
    const audit = createUniversalAuditTrailEntry({
      user: note.author,
      timestamp,
      recordType: note.recordType,
      recordId: note.recordId,
      fieldChanged: "business_note.create",
      previousValue: "None",
      newValue: summarizeNote(note),
      workspace: note.workspace,
      visibility: note.visibility,
    });
    updateOperationalState((current) => appendUniversalAuditEntry(appendUniversalActivity(upsertUniversalBusinessNote(current, note), activity), audit));
  }

  function handleDrawerActivity(tickler: UniversalTickler, description: string) {
    const timestamp = new Date().toISOString();
    const activity = createUniversalActivityEvent({
      workspace: tickler.workspace,
      recordType: tickler.recordType,
      recordId: tickler.recordId,
      user: tickler.assignedUser,
      role: "operator",
      timestamp,
      action: "Record detail activity added",
      description,
      outcome: tickler.status,
      visibility: "internal",
    });
    const audit = createUniversalAuditTrailEntry({
      user: tickler.assignedUser,
      timestamp,
      recordType: tickler.recordType,
      recordId: tickler.recordId,
      fieldChanged: "record_detail.activity",
      previousValue: "None",
      newValue: description,
      workspace: tickler.workspace,
      visibility: "internal",
    });
    updateOperationalState((current) => appendUniversalAuditEntry(appendUniversalActivity(current, activity), audit));
  }

  const statusItems = [
    { label: "Active notes", value: activeNotes.length },
    { label: "Updated this week", value: notesThisWeek.length },
    { label: "Archived notes", value: archivedNotes.length },
    { label: "Validation ticklers", value: validationTicklers.length },
  ];

  return (
    <BusinessCenterOSAdminShell
      title="Universal Business Notes Center"
      description="Create, edit, archive, filter, and validate universal business notes across non-AR workspaces using the shared operational engine."
      statusItems={statusItems}
    >
      <div className="grid min-w-0 gap-8">
        <section className="grid min-w-0 gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(320px,.8fr)]">
          <article className="business-center-os-card business-center-os-card--dark min-w-0 overflow-hidden rounded-3xl p-5 sm:p-6">
            <div className="border-b border-[rgba(6,182,212,.16)] pb-4">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">Business Notes</p>
              <h2 className="mt-1 font-display text-3xl font-bold">{editingNoteId ? "Edit note" : "Create note"}</h2>
              <p className="mt-2 text-sm font-semibold leading-7 text-[#CBD5E1]">{operationalStatus}</p>
            </div>
            <form onSubmit={handleSubmit} className="mt-5 grid gap-4">
              <label className="grid gap-2 text-sm font-extrabold text-[#F8FAFC]">
                Registered record
                <select
                  value={selectedRegistryRecord?.id || ""}
                  onChange={(event) => handleSelectRegistryRecord(event.target.value)}
                  className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold"
                >
                  <option value="">Select a registered record</option>
                  {registryRecords.filter((record) => isBusinessNoteRecordType(record.recordType)).map((record) => (
                    <option key={`${record.workspace}-${record.recordType}-${record.id}`} value={record.id}>
                      {record.recordName} / {formatLabel(record.workspace)} / {formatLabel(record.recordType)}
                    </option>
                  ))}
                </select>
              </label>
              {selectedRegistryRecord ? (
                <p className="rounded-2xl border border-[rgba(6,182,212,.16)] bg-[rgba(255,255,255,.04)] px-4 py-3 text-xs font-bold uppercase tracking-[0.12em] text-[#CBD5E1]">
                  Attached to {selectedRegistryRecord.recordName}
                  {selectedRegistryRecord.owner ? ` / Owner: ${selectedRegistryRecord.owner}` : ""}
                  {selectedRegistryRecord.status ? ` / Status: ${selectedRegistryRecord.status}` : ""}
                </p>
              ) : null}
              <div className="grid gap-4 md:grid-cols-2">
                <SelectField label="Workspace" value={form.workspace} onChange={(value) => setForm({ ...form, workspace: value as UniversalWorkspaceKey })} options={workspaceOptions} />
                <SelectField label="Record type" value={form.recordType} onChange={(value) => setForm({ ...form, recordType: value as BusinessNoteRecordType })} options={recordTypeOptions} />
                <label className="grid gap-2 text-sm font-extrabold text-[#F8FAFC]">
                  Record ID
                  <input value={form.recordId} onChange={(event) => setForm({ ...form, recordId: event.target.value })} className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-semibold" required />
                </label>
                <SelectField label="Category" value={form.noteCategory} onChange={(value) => setForm({ ...form, noteCategory: value as BusinessNoteCategory })} options={businessNoteCategories.map((category) => ({ value: category, label: category }))} />
                <SelectField label="Visibility" value={form.visibility} onChange={(value) => setForm({ ...form, visibility: value as UniversalVisibility })} options={visibilityOptions} />
              </div>
              <label className="grid gap-2 text-sm font-extrabold text-[#F8FAFC]">
                Content
                <textarea value={form.content} onChange={(event) => setForm({ ...form, content: event.target.value })} rows={6} className="business-center-os-field business-center-os-field--dark focus-ring rounded-2xl px-4 py-3 text-sm font-semibold" required />
              </label>
              <div className="flex flex-wrap gap-2">
                <button type="submit" disabled={!state || !form.content.trim() || !form.recordId.trim()} className="business-center-os-button business-center-os-button--primary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold disabled:opacity-60">
                  <Plus className="size-4" aria-hidden="true" />
                  {editingNoteId ? "Save Note" : "Create Note"}
                </button>
                <button type="button" onClick={resetForm} className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center rounded-full px-4 text-sm font-extrabold">
                  Reset
                </button>
              </div>
            </form>
          </article>

          <aside className="business-center-os-card business-center-os-card--light min-w-0 overflow-hidden rounded-3xl p-5 sm:p-6">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#14B8A6]">Detail View</p>
            <h2 className="mt-1 font-display text-3xl font-bold text-[#1F2937]">Selected note</h2>
            {selectedNote ? (
              <div className="mt-5 grid gap-3">
                <InfoBox label="Record" value={`${formatLabel(selectedNote.workspace)} / ${formatLabel(selectedNote.recordType)} / ${selectedNote.recordId}`} />
                <InfoBox label="Category" value={selectedNote.noteCategory} />
                <InfoBox label="Visibility" value={formatLabel(selectedNote.visibility)} />
                <InfoBox label="Status" value={selectedNote.status || "active"} />
                <div className="rounded-2xl border border-[rgba(20,184,166,.16)] bg-white p-4">
                  <p className="text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">Content</p>
                  <p className="mt-2 whitespace-pre-wrap text-sm font-semibold leading-7 text-[#475569]">{selectedNote.content}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => handleEditNote(selectedNote)} className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-10 items-center gap-2 rounded-full px-3 text-xs font-extrabold">
                    <Edit3 className="size-3" aria-hidden="true" />
                    Edit
                  </button>
                  <button type="button" disabled={selectedNote.status === "archived"} onClick={() => handleArchiveNote(selectedNote)} className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-10 items-center gap-2 rounded-full px-3 text-xs font-extrabold disabled:opacity-60">
                    <Archive className="size-3" aria-hidden="true" />
                    Archive
                  </button>
                </div>
              </div>
            ) : (
              <p className="mt-5 text-sm font-semibold leading-7 text-[#475569]">Select a note to inspect it here.</p>
            )}
          </aside>
        </section>

        <section className="business-center-os-card business-center-os-card--dark min-w-0 overflow-hidden rounded-3xl p-5 sm:p-6">
          <div className="flex flex-col gap-4 border-b border-[rgba(6,182,212,.16)] pb-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">Filters</p>
              <h2 className="mt-1 font-display text-3xl font-bold">Notes list</h2>
            </div>
            <label className="relative min-w-0 lg:w-80">
              <span className="sr-only">Search business notes</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#CBD5E1]" aria-hidden="true" />
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search notes..." className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl pl-10 pr-4 text-sm font-semibold" />
            </label>
          </div>
          <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-6">
            <FilterSelect label="Workspace" value={workspaceFilter} onChange={(value) => setWorkspaceFilter(value as UniversalWorkspaceKey | "all")} options={[{ value: "all", label: "All workspaces" }, ...workspaceOptions]} />
            <FilterSelect label="Record Type" value={recordTypeFilter} onChange={(value) => setRecordTypeFilter(value as BusinessNoteRecordType | "all")} options={[{ value: "all", label: "All record types" }, ...recordTypeOptions]} />
            <FilterSelect label="Category" value={categoryFilter} onChange={(value) => setCategoryFilter(value as BusinessNoteCategory | "all")} options={[{ value: "all", label: "All categories" }, ...businessNoteCategories.map((category) => ({ value: category, label: category }))]} />
            <FilterSelect label="Visibility" value={visibilityFilter} onChange={(value) => setVisibilityFilter(value as UniversalVisibility | "all")} options={[{ value: "all", label: "All visibility" }, ...visibilityOptions]} />
            <FilterSelect label="Author" value={authorFilter} onChange={setAuthorFilter} options={[{ value: "all", label: "All authors" }, ...authors.map((author) => ({ value: author, label: author }))]} />
            <FilterSelect label="Date Range" value={dateRangeFilter} onChange={(value) => setDateRangeFilter(value as DateRangeFilter)} options={[{ value: "all", label: "All dates" }, { value: "today", label: "Today" }, { value: "week", label: "Last 7 days" }, { value: "month", label: "Last 30 days" }]} />
            <FilterSelect label="State" value={archiveFilter} onChange={(value) => setArchiveFilter(value as ArchiveFilter)} options={[{ value: "active", label: "Active" }, { value: "archived", label: "Archived" }, { value: "all", label: "All" }]} />
          </div>
          <div className="mt-5 grid gap-3">
            {filteredNotes.length ? (
              filteredNotes.map((note) => (
                <article key={note.id} className="rounded-3xl border border-[rgba(6,182,212,.16)] bg-[rgba(255,255,255,.04)] p-4">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <button type="button" onClick={() => setSelectedNoteId(note.id)} className="focus-ring min-w-0 flex-1 rounded-2xl text-left">
                      <div className="flex flex-wrap gap-2">
                        <Badge label={note.noteCategory} />
                        <Badge label={formatLabel(note.workspace)} />
                        <Badge label={note.status || "active"} />
                      </div>
                      <p className="mt-3 text-sm font-semibold leading-7 text-[#E2E8F0]">{note.content}</p>
                      <p className="mt-3 text-xs font-bold uppercase tracking-[0.12em] text-[#CBD5E1]">
                        {formatLabel(note.recordType)} / {note.recordId} / {note.author} / {formatDate(note.updatedAt || note.timestamp)}
                      </p>
                    </button>
                    <div className="flex shrink-0 flex-wrap gap-2">
                      <button type="button" onClick={() => handleEditNote(note)} className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-10 items-center rounded-full px-3 text-xs font-extrabold">
                        Edit
                      </button>
                      <button type="button" disabled={note.status === "archived"} onClick={() => handleArchiveNote(note)} className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-10 items-center rounded-full px-3 text-xs font-extrabold disabled:opacity-60">
                        Archive
                      </button>
                    </div>
                  </div>
                </article>
              ))
            ) : (
              <div className="rounded-3xl border border-dashed border-[rgba(6,182,212,.24)] bg-[rgba(255,255,255,.03)] p-6">
                <p className="text-sm font-semibold leading-7 text-[#CBD5E1]">No business notes match the current filters.</p>
              </div>
            )}
          </div>
        </section>

        <section className="business-center-os-card business-center-os-card--dark min-w-0 overflow-hidden rounded-3xl p-5 sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">Non-AR Validation Record</p>
              <h2 className="mt-1 font-display text-3xl font-bold">{validationRecord.title}</h2>
              <p className="mt-2 max-w-3xl text-sm font-semibold leading-7 text-[#CBD5E1]">
                This Business Center OS project record proves universal notes, activity, ticklers, audit history, and the drawer work outside CollabRecover AR Solutions.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={handleCreateValidationTickler} className="business-center-os-button business-center-os-button--primary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold">
                <ClipboardCheck className="size-4" aria-hidden="true" />
                Create Tickler
              </button>
              <button type="button" disabled={!selectedDrawerTickler} onClick={() => selectedDrawerTickler && handleTicklerStatus(selectedDrawerTickler, "Completed")} className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold disabled:opacity-60">
                <CheckCircle2 className="size-4" aria-hidden="true" />
                Complete Tickler
              </button>
              <button type="button" disabled={!selectedDrawerTickler} onClick={() => setDrawerOpen(true)} className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center rounded-full px-4 text-sm font-extrabold disabled:opacity-60">
                Open Drawer
              </button>
            </div>
          </div>
          <div className="mt-5 grid gap-4 md:grid-cols-4">
            <InfoTile label="Workspace" value={formatLabel(validationRecord.workspace)} />
            <InfoTile label="Record Type" value={formatLabel(validationRecord.recordType)} />
            <InfoTile label="Record ID" value={validationRecord.recordId} />
            <InfoTile label="Ticklers" value={validationTicklers.length.toString()} />
          </div>
        </section>
      </div>

      <UniversalOperationalRecordDrawer
        open={drawerOpen}
        tickler={selectedDrawerTickler}
        state={state || emptyOperationalState()}
        persistenceStatus={operationalStatus}
        relatedRoute="/admin/truevance-project-center"
        onClose={() => setDrawerOpen(false)}
        onStatusChange={handleTicklerStatus}
        onAddNote={handleDrawerNote}
        onAddActivity={handleDrawerActivity}
      />
    </BusinessCenterOSAdminShell>
  );
}

function SelectField({ label, value, onChange, options }: { label: string; value: string; onChange: (value: string) => void; options: Array<{ value: string; label: string }> }) {
  return (
    <label className="grid gap-2 text-sm font-extrabold text-[#F8FAFC]">
      {label}
      <select value={value} onChange={(event) => onChange(event.target.value)} className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold">
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </label>
  );
}

function FilterSelect(props: Parameters<typeof SelectField>[0]) {
  return <SelectField {...props} />;
}

function InfoBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-[rgba(20,184,166,.16)] bg-white p-4">
      <p className="text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">{label}</p>
      <p className="mt-2 break-words text-sm font-bold leading-6 text-[#1F2937]">{value}</p>
    </div>
  );
}

function InfoTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-[rgba(6,182,212,.16)] bg-[rgba(255,255,255,.04)] p-4">
      <p className="text-xs font-black uppercase tracking-[0.14em] text-[#06B6D4]">{label}</p>
      <p className="mt-2 break-words text-sm font-semibold leading-6 text-[#E2E8F0]">{value}</p>
    </div>
  );
}

function Badge({ label }: { label: string }) {
  return <span className="rounded-full border border-[rgba(6,182,212,.22)] bg-[rgba(6,182,212,.08)] px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-[#BAE6FD]">{label}</span>;
}

function summarizeNote(note: UniversalBusinessNote) {
  return `${note.noteCategory}: ${note.content.slice(0, 120)}`;
}

function formatLabel(value: string) {
  return value
    .replace(/[-_]/g, " ")
    .replace(/\bos\b/gi, "OS")
    .replace(/\bar\b/gi, "AR")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function isBusinessNoteRecordType(recordType: UniversalRecordType): recordType is BusinessNoteRecordType {
  return !["ar_account", "ar_promise", "recovery_record"].includes(recordType);
}

function emptyOperationalState(): UniversalOperationalFoundationState {
  return {
    activityEvents: [],
    businessNotes: [],
    ticklers: [],
    auditTrail: [],
    arRecoveryNotes: [],
    collectionLetterDrafts: [],
    collectionLetterHistory: [],
    operationalRecords: [],
  };
}
